/**
 * Fair Home V2 — fit every pager slide into the viewport height.
 *
 * Sections are drawn for the 1440×900 mock. On laptop windows (≈600–860 tall)
 * several of them — capsules, services, the AI block with its overhanging
 * mascot, the footer — ran taller than the 100dvh slide, which then turned into
 * its own scrollport (FE-004) and fought the pager for the wheel. Instead, the
 * section is zoomed down just enough to fit: proportions stay those of the mock,
 * percentage widths still span the slide, and the wheel goes straight to the
 * pager.
 *
 * Below `MIN_SCALE` (Windows at 125–150 % OS scale) shrinking further would hurt
 * legibility, so the section stops there and the FE-004 scrollport takes the
 * rest. Hero is excluded — its canvas scales by itself and is meant to crop.
 * Compact and reduced-motion layouts are not paged and are left alone.
 */
import { nextTick, onBeforeUnmount, watch, type Ref } from 'vue'
import { logPagerFit } from './fairHomeV2PagerDebug'

/** Smallest zoom before the slide falls back to scrolling. */
const MIN_SCALE = 0.7

/** Sub-pixel slack: zoom rounding must not leave a 1px scrollbar behind. */
const FIT_SLACK_PX = 1

/** A zoomed section re-fits only once it has more room than this to grow back. */
const REFIT_ROOM_PX = 8

const SLIDE_INNER_SELECTOR =
  '.fair-home-v2__slide:not(.fair-home-v2__slide--hero) .fair-home-v2__slide-inner'

function fitSlide(inner: HTMLElement) {
  const content = inner.firstElementChild as HTMLElement | null
  if (!content) return

  const available = inner.clientHeight
  // Already settled — re-measuring would reset the zoom and wake every observer
  // for nothing, and a section whose layout follows its zoom would never rest.
  if (inner.scrollHeight <= available + FIT_SLACK_PX) {
    if (!content.style.getPropertyValue('zoom')) return
    if (available - content.getBoundingClientRect().height <= REFIT_ROOM_PX) return
  }

  // Measure the section at its natural size.
  content.style.removeProperty('zoom')
  inner.style.overflowY = ''
  const natural = inner.scrollHeight
  if (natural <= available + FIT_SLACK_PX) {
    logPagerFit(inner, { natural, available, zoom: '1' })
    return
  }

  const target = available - FIT_SLACK_PX
  const scale = applyZoom(content, target / natural)
  // Width-derived heights (aspect-ratio media, % sizes) do not shrink with zoom,
  // so the first guess can fall short — correct once by what is actually left.
  if (inner.scrollHeight > available + FIT_SLACK_PX) {
    applyZoom(content, scale * (target / inner.scrollHeight))
  }
  // Fully fitted — no scrollbar gutter, nothing for the wheel to scroll.
  // Otherwise the FE-004 scrollport keeps the rest reachable.
  if (inner.scrollHeight <= available + FIT_SLACK_PX) inner.style.overflowY = 'hidden'
  logPagerFit(inner, {
    natural,
    available,
    zoom: content.style.getPropertyValue('zoom'),
    overflowAfter: inner.scrollHeight - available,
  })
}

function applyZoom(content: HTMLElement, scale: number): number {
  const zoom = Math.max(MIN_SCALE, Math.floor(scale * 1000) / 1000)
  content.style.setProperty('zoom', String(zoom))
  return zoom
}

function resetSlide(inner: HTMLElement) {
  const content = inner.firstElementChild as HTMLElement | null
  if (content) content.style.removeProperty('zoom')
  inner.style.overflowY = ''
}

export function useFairHomeV2SlideFit(
  rootRef: Ref<HTMLElement | null>,
  enabled: Ref<boolean>,
) {
  let inners: HTMLElement[] = []
  let frame = 0

  const resizeObserver =
    typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleFit)
  // Sections swap parts in and out (skeleton → cards, the pager bar appearing once
  // there is more than one page) — new nodes need observing and a fresh fit.
  const mutationObserver =
    typeof MutationObserver === 'undefined' ? null : new MutationObserver(observe)

  function scheduleFit() {
    if (frame) return
    frame = requestAnimationFrame(() => {
      frame = 0
      inners.forEach(fitSlide)
    })
  }

  function observe() {
    resizeObserver?.disconnect()
    for (const inner of inners) {
      resizeObserver?.observe(inner)
      const content = inner.firstElementChild
      if (!content) continue
      // The section's own box can stay put while a part of it grows past it.
      resizeObserver?.observe(content)
      for (const part of Array.from(content.children)) resizeObserver?.observe(part)
    }
    scheduleFit()
  }

  function detach() {
    resizeObserver?.disconnect()
    mutationObserver?.disconnect()
    if (frame) cancelAnimationFrame(frame)
    frame = 0
    inners.forEach(resetSlide)
    inners = []
  }

  async function attach() {
    detach()
    if (!enabled.value) return
    await nextTick()
    const root = rootRef.value
    if (!root) return
    inners = Array.from(root.querySelectorAll<HTMLElement>(SLIDE_INNER_SELECTOR))
    for (const inner of inners) {
      mutationObserver?.observe(inner, { childList: true, subtree: true })
    }
    observe()
  }

  watch([rootRef, enabled], attach, { immediate: true, flush: 'post' })

  onBeforeUnmount(detach)
}
